/**
 * useSessionTimer Hook
 * 
 * Manages session timing - tracks elapsed time and provides formatted duration.
 * Properly handles pause/resume by tracking cumulative pause duration.
 */

import { useState, useEffect, useRef } from 'react';
import { logger } from '../utils/logger';

interface UseSessionTimerReturn {
  duration: number; // Duration in seconds
  formattedDuration: string; // Formatted as MM:SS
}

export function useSessionTimer(
  isActive: boolean,
  startTime: Date | null,
  isPaused: boolean = false
): UseSessionTimerReturn {
  const [duration, setDuration] = useState(0);
  const [totalPausedTime, setTotalPausedTime] = useState(0); // Track cumulative pause time
  const pauseStartTimeRef = useRef<number | null>(null); // Track when pause started

  // Track pause/resume to calculate paused duration
  useEffect(() => {
    if (isPaused) {
      // Started pausing - record pause start time
      if (pauseStartTimeRef.current === null) {
        pauseStartTimeRef.current = Date.now();
        logger.debug('[Timer] Paused at:', new Date(pauseStartTimeRef.current).toISOString());
      }
    } else {
      // Resumed - add pause duration to total
      if (pauseStartTimeRef.current !== null) {
        const pauseDuration = Date.now() - pauseStartTimeRef.current;
        setTotalPausedTime(prev => {
          const newTotal = prev + pauseDuration;
          logger.debug('[Timer] Resumed - Pause duration:', Math.floor(pauseDuration / 1000), 's, Total paused:', Math.floor(newTotal / 1000), 's');
          return newTotal;
        });
        pauseStartTimeRef.current = null;
      }
    }
  }, [isPaused]);

  // Timer effect - update duration every second when active AND not paused
  useEffect(() => {
    if (!isActive || !startTime) return;

    const interval = setInterval(() => {
      // Calculate elapsed time, subtracting total paused time
      let currentPausedTime = totalPausedTime;
      
      // If currently paused, add current pause duration
      if (isPaused && pauseStartTimeRef.current !== null) {
        currentPausedTime += Date.now() - pauseStartTimeRef.current;
      }
      
      const elapsed = Math.floor((new Date().getTime() - startTime.getTime() - currentPausedTime) / 1000);
      setDuration(elapsed);
    }, 1000);

    return () => clearInterval(interval);
  }, [isActive, startTime, isPaused, totalPausedTime]);

  // Format duration as MM:SS
  const formattedDuration = (() => {
    const mins = Math.floor(duration / 60);
    const secs = duration % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  })();

  return {
    duration,
    formattedDuration
  };
}

